import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import RiskIndexInfoDrawerView from '@app/src/components/Drawer/Views/RiskIndexInfoDrawerView'
import ButtonCell from '@app/src/components/Table/Cells/ButtonCell'
import CategoryCell from '@app/src/components/Table/Cells/CategoryCell'
import SwitchCell from '@app/src/components/Table/Cells/SwitchCell'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import type { OrganizationRiskSetting } from '@app/src/types/resourceExplorer'
import { RiskIndexType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { Factory } from '@mui/icons-material'
import { alpha, ListItem, ListItemAvatar, ListItemText, TableCell, TableRow } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import RiskIndexIcon from './RiskIndexIcons/RiskIndexIcon'

interface RiskToggleFormData {
  isActivated: boolean
}

interface RiskIndexIconProps {
  row: OrganizationRiskSetting
  isEditMode?: boolean
  onToggleChange?: (riskIndexId: number, isActivated: boolean) => void
  modifiedRiskStates?: Map<number, boolean>
}

export const RiskSettingsRow: React.FC<RiskIndexIconProps> = ({
  row: riskSetting,
  isEditMode = false,
  onToggleChange,
  modifiedRiskStates,
}: RiskIndexIconProps) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { trackEvent } = useAmplitude()
  const riskIndexId = riskSetting.riskType.id

  const parentValue =
    modifiedRiskStates?.has(riskIndexId) && modifiedRiskStates.get(riskIndexId) !== undefined
      ? modifiedRiskStates.get(riskIndexId)
      : riskSetting.isActivated

  const { control, reset } = useForm<RiskToggleFormData>({
    defaultValues: {
      isActivated: parentValue ?? false,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    reset({ isActivated: parentValue ?? false })
  }, [parentValue, reset])

  const isIndustryRisk = riskSetting.riskType.riskIndexType === RiskIndexType.Industry

  return (
    <>
      <TableRow>
        {isEditMode ? (
          <Controller
            control={control}
            name="isActivated"
            render={({ value, onChange }) => (
              <SwitchCell
                checked={value}
                onChange={() => {
                  const newValue = !value
                  onChange(newValue)

                  trackEvent({
                    name: AmplitudeTrackingEvents.Organization.RiskIndex.Toggled,
                    eventProps: {
                      name: riskSetting.riskType.name,
                      enabled: newValue,
                    },
                  })

                  if (onToggleChange) {
                    setTimeout(() => {
                      onToggleChange(riskIndexId, newValue)
                    }, 50)
                  }
                }}
              />
            )}
          />
        ) : parentValue ? (
          <TableCell width={20}>{formatMessage({ id: 'automations.on' })}</TableCell>
        ) : (
          <TableCell
            width={20}
            sx={{
              color: 'text.disabled',
            }}
          >
            {formatMessage({ id: 'automations.off' })}
          </TableCell>
        )}
        <TableCell>
          <ListItem component="div">
            <ListItemAvatar>
              <Avatar
                size={AvatarSize.XXL}
                sx={({ palette }) => ({
                  backgroundColor: !isIndustryRisk ? 'white' : alpha(palette.secondary.main, 0.1),
                  border: !isIndustryRisk ? `1px solid ${palette.secondary.main}` : 'none',
                  mr: 2,
                })}
              >
                {isIndustryRisk ? (
                  <Factory fontSize="large" color="secondary" />
                ) : (
                  <RiskIndexIcon source={riskSetting.riskType.source} />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={formatMessage({ id: `schemas.risk.riskIndexes.${riskSetting.riskType.source}.label` })}
              secondary={formatMessage({ id: `schemas.risk.riskIndexes.${riskSetting.riskType.source}.source` })}
            />
          </ListItem>
        </TableCell>
        <CategoryCell
          value={formatMessage({ id: `schemas.risk.esgArea.${riskSetting.riskType.sustainabilityArea}` })}
        />
        <CategoryCell value={formatMessage({ id: `schemas.risk.indexType.${riskSetting.riskType.riskIndexType}` })} />
        <ButtonCell
          label={formatMessage({ id: 'general.showMore' })}
          onClick={() => openDrawer(<RiskIndexInfoDrawerView riskType={riskSetting.riskType} />)}
        />
      </TableRow>
    </>
  )
}

export default RiskSettingsRow
